import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import { useAuth } from '../context/AuthContext.js';

function CreateAthlete({ addAthlete }) {
  const { user } = useAuth();
  const [mode, setMode] = useState('single'); // 'single' | 'bulk'
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [emergencyContactName, setEmergencyContactName] = useState('');
  const [emergencyContactPhone, setEmergencyContactPhone] = useState('');
  const [usagId, setUsagId] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      setError('Please log in again.');
      return;
    }

    if (!['admin', 'coach'].includes(user.role)) {
      setError('You do not have permission to create athletes.');
      return;
    }

    axios.defaults.headers.common['Authorization'] = `Bearer ${user.token}`;
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user || !user.teamId) {
      setError('User team information is missing. Please log in again.');
      return;
    }

    if (!name) {
      setError('Athlete name is required.');
      return;
    }

    const newAthlete = {
      name,
      dob: dob || undefined,
      height: height || undefined,
      weight: weight || undefined,
      email: email || undefined,
      phone: phone || undefined,
      emergency_contact_name: emergencyContactName || undefined,
      emergency_contact_phone: emergencyContactPhone || undefined,
      usag_id: usagId || undefined,
      teamId: user.teamId
    };

    try {
      if (addAthlete) {
        await addAthlete(newAthlete);
      } else {
        await axios.post(`${API_BASE_URL}/athletes`, newAthlete);
      }

      setName(''); setDob(''); setHeight(''); setWeight('');
      setEmail(''); setPhone(''); setEmergencyContactName('');
      setEmergencyContactPhone(''); setUsagId(''); setError('');

      navigate('/athletes');
    } catch (err) {
      console.error('Error adding athlete:', err);
      setError(err.response?.data?.error || 'Failed to add athlete.');
    }
  };

  if (error && mode === 'single') return <p style={{ color: 'red' }}>{error}</p>;

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 12px' }}>
      <h2>Add Athletes</h2>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid #e1e1e1' }}>
        <TabButton active={mode === 'single'} onClick={() => setMode('single')}>Single Athlete</TabButton>
        <TabButton active={mode === 'bulk'} onClick={() => setMode('bulk')}>Bulk Import (Excel/CSV)</TabButton>
      </div>

      {mode === 'single' ? (
        <form onSubmit={handleSubmit}>
          <div><label>Name:</label><input type="text" value={name} onChange={e => setName(e.target.value)} required /></div>
          <div><label>Date of Birth:</label><input type="date" value={dob} onChange={e => setDob(e.target.value)} /></div>
          <div><label>Height (cm):</label><input type="number" min="0" value={height} onChange={e => setHeight(e.target.value)} /></div>
          <div><label>Weight (kg):</label><input type="number" min="0" value={weight} onChange={e => setWeight(e.target.value)} /></div>
          <div><label>Email:</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} /></div>
          <div><label>Phone:</label><input type="tel" value={phone} onChange={e => setPhone(e.target.value)} /></div>
          <div><label>Emergency Contact Name:</label><input type="text" value={emergencyContactName} onChange={e => setEmergencyContactName(e.target.value)} /></div>
          <div><label>Emergency Contact Phone:</label><input type="tel" value={emergencyContactPhone} onChange={e => setEmergencyContactPhone(e.target.value)} /></div>
          <div><label>USAG ID:</label><input type="text" value={usagId} onChange={e => setUsagId(e.target.value)} placeholder="e.g. 1234567" maxLength={20} /></div>
          <button type="submit">Add Athlete</button>
        </form>
      ) : (
        <BulkImport teamId={user?.teamId} onDone={() => navigate('/athletes')} />
      )}
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'none', border: 'none', cursor: 'pointer',
        padding: '10px 14px', fontSize: '0.95rem',
        fontWeight: active ? 700 : 400,
        color: active ? '#1a5276' : '#555',
        borderBottom: active ? '3px solid #1a5276' : '3px solid transparent'
      }}
    >
      {children}
    </button>
  );
}

// ── Column header → athlete field resolver ─────
function resolveField(header) {
  const h = String(header || '').toLowerCase().replace(/[^a-z]/g, '');
  if (h.includes('emergency') && h.includes('name')) return 'emergency_contact_name';
  if (h.includes('emergency') && h.includes('phone')) return 'emergency_contact_phone';
  if (h.includes('usag')) return 'usag_id';
  if (h.includes('email')) return 'email';
  if (h.includes('phone')) return 'phone';
  if (h.includes('height')) return 'height';
  if (h.includes('weight')) return 'weight';
  if (h.includes('dob') || h.includes('birth')) return 'dob';
  if (h.includes('name')) return 'name';
  return null;
}

function normalizeDob(value) {
  if (value === '' || value == null) return { value: '' };
  if (value instanceof Date && !isNaN(value)) {
    return { value: toISO(value) };
  }
  const d = new Date(value);
  if (!isNaN(d) && /\d/.test(String(value))) return { value: toISO(d) };
  return { error: `Unrecognized date "${value}"` };
}

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const TEMPLATE_HEADERS = [
  'Name', 'Date of Birth (YYYY-MM-DD)', 'USAG #', 'Email', 'Phone',
  'Height (cm)', 'Weight (kg)', 'Emergency Contact Name', 'Emergency Contact Phone'
];

function BulkImport({ teamId, onDone }) {
  const [rows, setRows] = useState([]);      // parsed + validated rows
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    const XLSX = await import('xlsx');
    const ws = XLSX.utils.aoa_to_sheet([
      TEMPLATE_HEADERS,
      ['Jane Smith', '2012-05-14', '1234567', 'parent@example.com', '555-123-4567', '', '', 'John Smith', '555-123-4567']
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Roster');
    XLSX.writeFile(wb, 'athlete_roster_template.xlsx');
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParseError(''); setResult(null); setFileName(file.name);
    try {
      const XLSX = await import('xlsx');
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array', cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { defval: '', raw: true });
      if (!raw.length) { setParseError('That sheet has no data rows.'); setRows([]); return; }

      const seenUsag = new Set();
      const parsed = raw.map((r, idx) => {
        const athlete = {};
        Object.keys(r).forEach(key => {
          const field = resolveField(key);
          if (field) athlete[field] = typeof r[key] === 'string' ? r[key].trim() : r[key];
        });

        const messages = [];
        let status = 'ok';

        const nm = String(athlete.name || '').trim();
        if (!nm) { status = 'error'; messages.push('Missing name'); }

        if (athlete.dob) {
          const d = normalizeDob(athlete.dob);
          if (d.error) { status = 'error'; messages.push(d.error); }
          else athlete.dob = d.value;
        } else {
          athlete.dob = '';
        }

        const usag = String(athlete.usag_id || '').trim();
        if (!usag) {
          if (status !== 'error') status = 'warning';
          messages.push('No USAG # (required for competition athletes)');
        } else if (seenUsag.has(usag)) {
          if (status !== 'error') status = 'warning';
          messages.push('Duplicate USAG # in this file');
        } else {
          seenUsag.add(usag);
        }

        return { _row: idx + 2, athlete, status, messages };
      });

      setRows(parsed);
    } catch (err) {
      console.error('Parse error:', err);
      setParseError('Could not read that file. Make sure it is a valid .xlsx or .csv.');
      setRows([]);
    }
  };

  const importable = rows.filter(r => r.status !== 'error');

  const doImport = async () => {
    if (!teamId) { setParseError('Your team info is missing — please log in again.'); return; }
    if (!importable.length) return;
    setImporting(true); setParseError('');
    try {
      const payload = importable.map(r => ({
        _row: r._row,
        name: r.athlete.name,
        dob: r.athlete.dob || undefined,
        usag_id: r.athlete.usag_id || undefined,
        email: r.athlete.email || undefined,
        phone: r.athlete.phone != null ? String(r.athlete.phone) : undefined,
        height: r.athlete.height || undefined,
        weight: r.athlete.weight || undefined,
        emergency_contact_name: r.athlete.emergency_contact_name || undefined,
        emergency_contact_phone: r.athlete.emergency_contact_phone != null ? String(r.athlete.emergency_contact_phone) : undefined
      }));
      const { data } = await api.post(`${API_BASE_URL}/athletes/bulk`, { athletes: payload, teamId });
      const clientSkipped = rows.filter(r => r.status === 'error')
        .map(r => ({ row: r._row, name: r.athlete.name || '', reason: r.messages.join('; ') }));
      setResult({
        created_count: data.created_count,
        skipped: [...(data.skipped || []), ...clientSkipped]
      });
      setRows([]);
    } catch (err) {
      setParseError(err.response?.data?.error || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  const statusStyle = {
    ok: { color: '#27ae60' },
    warning: { color: '#e67e22' },
    error: { color: '#c0392b' }
  };

  if (result) {
    return (
      <div>
        <div style={{ padding: 16, background: '#eafaf1', borderRadius: 8, marginBottom: 16 }}>
          ✅ Imported <strong>{result.created_count}</strong> athlete(s).
          {result.skipped.length > 0 && <> {result.skipped.length} skipped.</>}
        </div>
        {result.skipped.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h4>Skipped rows</h4>
            <ul>
              {result.skipped.map((s, i) => (
                <li key={i}>Row {s.row}{s.name ? ` (${s.name})` : ''}: {s.reason}</li>
              ))}
            </ul>
          </div>
        )}
        <button onClick={onDone}>View Roster</button>
        <button style={{ marginLeft: 8 }} onClick={() => { setResult(null); setFileName(''); }}>
          Import Another File
        </button>
      </div>
    );
  }

  return (
    <div>
      <p style={{ color: '#555' }}>
        Upload a spreadsheet to add your whole roster at once. Start from the template so the columns line up.
      </p>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <button type="button" onClick={downloadTemplate}>⬇ Download Template (.xlsx)</button>
        <label style={{ display: 'inline-block' }}>
          <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} style={{ display: 'inline-block' }} />
        </label>
        {fileName && <span style={{ color: '#777', fontSize: '0.9rem' }}>{fileName}</span>}
      </div>

      {parseError && <div style={{ color: '#c0392b', marginBottom: 12 }}>{parseError}</div>}

      {rows.length > 0 && (
        <>
          <div style={{ marginBottom: 8, fontSize: '0.9rem', color: '#555' }}>
            {importable.length} of {rows.length} row(s) will import.
            {rows.some(r => r.status === 'error') && ' Rows with errors are skipped.'}
          </div>
          <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #e1e1e1', borderRadius: 6 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ background: '#f7f7f7', position: 'sticky', top: 0 }}>
                  <th style={th}>Row</th>
                  <th style={th}>Name</th>
                  <th style={th}>DOB</th>
                  <th style={th}>USAG #</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._row} style={{ borderTop: '1px solid #eee' }}>
                    <td style={td}>{r._row}</td>
                    <td style={td}>{r.athlete.name || <em style={{ color: '#c0392b' }}>—</em>}</td>
                    <td style={td}>{r.athlete.dob || ''}</td>
                    <td style={td}>{r.athlete.usag_id || ''}</td>
                    <td style={{ ...td, ...statusStyle[r.status] }}>
                      {r.status === 'ok' ? 'Ready' : `${r.status === 'error' ? 'Error' : 'Warning'}: ${r.messages.join('; ')}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 16 }}>
            <button type="button" onClick={doImport} disabled={importing || !importable.length}>
              {importing ? 'Importing...' : `Import ${importable.length} Athlete(s)`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const th = { textAlign: 'left', padding: '8px 10px', fontWeight: 600 };
const td = { padding: '6px 10px' };

export default CreateAthlete;
